import { useMemo } from "react";

// Components
import CustomIcon from "components/CustomIcon";
import VcpImportance from "components/Projection/VcpImportance";
import MessageUpgrade from "components/Pro/Messages/MessageUpgrade/index";
import { Grid, Button, IconButton, Typography, Box, Switch, FormControlLabel } from "@mui/material";
import { ChartLSPMScoreError } from "components/Charts/components/ChartErrorModal";
import { ChatBot } from "components/Chatbot";

// Hooks
import { useIntl } from "react-intl";
import { useAppSelector } from "app/hooks";
import useCharts from "./useCharts";
import useGraphsFetch from "utils/hooks/useGraphsFetch";

// Store
import { BalanceSerie, BubbleSerie, getCharts, getLD5 } from "store/slice/charts";

// Ability
import { useAbility } from "@casl/react";
import { AbilityContext, compoundPermissions } from "context/Ability";

// Others
import icons from "enums/icons";

// Utils
import classnames from "classnames";
import cn from "classnames";

// Styles
import ChartStyles from "./Charts.styles";
import { BBNoDataError } from "components/Charts/BehaviouralBreakdown/ErrorMessages";
import { Legend } from "components/Charts/Tenure/components/Legend";
import { TenureLabelsProvider } from "components/Charts/Tenure/context/TenureLabelsContext";

import { IBalanceCandidate, IBalanceSituation } from "services";
import { useAuth } from "utils/hooks";

const Charts = (): JSX.Element => {
    const intl = useIntl();
    const ability = useAbility(AbilityContext);
    const allChartData = useAppSelector(getCharts).data;

    const { findOrgRoleByName } = useAuth();
    const canSeeChatBot = findOrgRoleByName("LD_SAAS_INTERNAL");

    // Extract candidates data from balance and lspm weights from graph data to create chatbot payload.
    const graphData = useGraphsFetch();
    const ld5ChartData = useAppSelector(getLD5);
    const targetCompanyId = graphData.target_company_id;
    const situation: IBalanceSituation | null = graphData.lspm_weighting?.situation || null;
    const isBubbleSerie = (serie: BalanceSerie): serie is BubbleSerie => {
        return "custom" in serie;
    };

    const candidates = useMemo<IBalanceCandidate[]>(() => {
        const targetCompanySeries = ld5ChartData?.series
            .filter(isBubbleSerie)
            .find((serie) => serie.custom.entity_type === "company" && serie.custom.entity_id === targetCompanyId);

        if (!targetCompanySeries?.data) {
            return [];
        }

        return targetCompanySeries.data
            .filter((point) => point.custom?.entity_type === "person")
            .map((point) => ({
                name: String(point.custom?.entity_name ?? point.name ?? ""),
                role: String(point.custom?.current_job_title ?? point.custom?.label ?? ""),
                size: Number(point.z ?? 0),
                x: Number(point.x ?? 0),
                y: Number(point.y ?? 0),
            }));
    }, [ld5ChartData, targetCompanyId]);

    const {
        isFetchingCharts,
        width,
        height,
        reportChart,
        shouldShowPaceDefinitions,
        insightsSectionRef,
        errorData,
        behaviouralBreakdownData,
        chartWrapper,
        handleOnClickInsightsLink,
        goToReport,
        setShowPaceDefinitions,
    } = useCharts();

    const canSeeChart = reportChart ? compoundPermissions(reportChart.permissions, ability) : false;
    const chartExplainer = reportChart && allChartData?.graphs[reportChart.id]?.raw?.leadership_insight?.explainer;
    const isChatbotVisible = reportChart?.id === "LD5";

    const classes = ChartStyles({ canSeeChart });

    const projectionContent = (
        <Grid container className={classes.projectionBodyWrapper}>
            {!isFetchingCharts && (
                <Grid
                    item
                    ref={chartWrapper}
                    className={cn(classes.projectionBodyLeftSide, { [classes.chatVisible]: isChatbotVisible })}
                >
                    {!canSeeChart && (
                        <Box className={classes.proGraphMsgWrapper} data-id={reportChart?.id}>
                            <MessageUpgrade
                                variant="type2"
                                icon={icons.proStar}
                                title={"pro.message.heading"}
                                subtitle={"pro.upgrade"}
                                descriptionTitle={"pro.message.details.heading"}
                            >
                                {Array.isArray(chartExplainer) ? (
                                    chartExplainer.map((paragraph, index) => (
                                        <Box component="p" key={index}>
                                            {paragraph}
                                        </Box>
                                    ))
                                ) : chartExplainer ? (
                                    <Box component="p">{chartExplainer}</Box>
                                ) : null}
                            </MessageUpgrade>
                        </Box>
                    )}

                    {canSeeChart &&
                        reportChart?.graphComponent(width, height, {
                            showPaceDefinitions: shouldShowPaceDefinitions,
                            setShowPaceDefinitions,
                            ...(reportChart.id === "LD12"
                                ? {
                                      fallbackComponent: <BBNoDataError />,
                                      fallbackCondition: Boolean(!behaviouralBreakdownData?.series_labels?.length),
                                  }
                                : {}),
                        })}

                    {Boolean(errorData.length) && <ChartLSPMScoreError errorData={errorData} />}
                </Grid>
            )}

            <Grid item ref={insightsSectionRef} className={classes.projectionBodyRightSide}>
                <VcpImportance />
                {reportChart?.graphInsights}

                {/* Tenure Chart Legend */}
                {reportChart?.id === "LD13" && <Legend />}
            </Grid>
        </Grid>
    );

    return (
        <>
            <Grid container className={classes.headerContainer}>
                <Grid container item className={classes.navBox}>
                    <Grid container item className={classnames(classes.navControls, classes.navBtnLeft)}>
                        <IconButton onClick={() => goToReport("previous")} className={classes.navBtnLeft} size="medium">
                            <CustomIcon className={classes.navControlIcon} icon={icons.circledChevronLeft} />
                        </IconButton>
                    </Grid>
                    <Grid item className={classes.navReportTitleBox}>
                        <Typography component="span" className={classes.navReportTitle}>
                            {reportChart?.subNavLabel}
                        </Typography>
                    </Grid>
                    <Grid container item className={classnames(classes.navControls, classes.navBtnRight)}>
                        <IconButton onClick={() => goToReport("next")} className={classes.navBtnRight} size="medium">
                            <CustomIcon className={classes.navControlIcon} icon={icons.circledChevronRight} />
                        </IconButton>
                    </Grid>
                </Grid>
                {(reportChart?.id === "LD12" || reportChart?.id === "LD8") && (
                    <Grid item className={classes.paceDefinitionWrapper}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={shouldShowPaceDefinitions}
                                    disabled={Boolean(
                                        reportChart?.id === "LD12" &&
                                            !allChartData?.graphs.LD12?.converted?.series_labels.length
                                    )}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        setShowPaceDefinitions(event.target.checked)
                                    }
                                />
                            }
                            label={intl.formatMessage({ id: "projection.pace.definitions.label" })}
                        />
                        <Box className={classes.secondaryPanel}>
                            <Button
                                className={classes.btnToInsights}
                                onClick={handleOnClickInsightsLink}
                                startIcon={<CustomIcon icon={icons.circledLightBulb} />}
                            >
                                {intl.formatMessage({
                                    id: "projection.insights.insights",
                                })}
                            </Button>
                        </Box>
                    </Grid>
                )}
            </Grid>

            {reportChart?.id === "LD13" ? (
                <TenureLabelsProvider>{projectionContent}</TenureLabelsProvider>
            ) : (
                projectionContent
            )}

            {canSeeChatBot && isChatbotVisible && (
                <ChatBot candidatesPayload={candidates} situationPayload={situation} />
            )}
        </>
    );
};

export default Charts;
